import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PDFModal from '../PDFModal/PDFModal';
import ReportModal from '../ReportModal/ReportModal';
import './DocumentList.css';

const DocumentList = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [summaryDocuments, setSummaryDocuments] = useState(new Map()); // Store summary docs separately

  // Get backend URL for API calls - using the same logic as other working tabs
  const getBackendUrl = () => {
    // First, try React environment variables
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Second, try regular environment variables
    if (process.env.BACKEND_URL) {
      return process.env.BACKEND_URL;
    }
    
    // Third, check if we're running on Firebase Hosting and use Cloud Run backend
    if (typeof window !== 'undefined' && window.location.hostname) {
      const hostname = window.location.hostname;
      
      // Check for Firebase domains (including the current working one)
      if (hostname.includes('firebaseapp.com') || 
          hostname.includes('web.app') ||
          hostname.includes('viraltrade.in') ||
          hostname.includes('viraltrade-19032')) {
        console.log('🔍 DocumentList: Detected Firebase domain, using Cloud Run backend');
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
      
      // Fourth, check if we're running on Cloud Run and use hardcoded URL
      if (hostname.includes('run.app')) {
        console.log('🔍 DocumentList: Detected Cloud Run domain, using Cloud Run backend');
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
    }
    
    // Finally, fallback to localhost for development
    console.log('🔍 DocumentList: Using localhost fallback for development');
    return 'http://localhost:8000';
  };

  const BASE_URL = getBackendUrl();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = `${BASE_URL}/api/fetchDocuments`;
      console.log('🔍 DocumentList: Fetching documents from:', apiUrl);
      console.log('🔍 DocumentList: BASE_URL resolved to:', BASE_URL);
      
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Separate original documents from summary documents
      const originalDocs = [];
      const summaryDocsMap = new Map();

      data.documents.forEach(doc => {
        const filename = doc.filename;

        // Check if this is a summary document
        if (filename.includes('-summary')) {
          // Extract the original filename by removing '-summary' and '.pdf'
          const originalFilename = filename.replace('-summary.pdf', '.pdf');
          summaryDocsMap.set(originalFilename, doc);
        } else {
          // This is an original document
          originalDocs.push(doc);
        }
      });

      // Transform only the original documents for display
      const transformedDocuments = originalDocs.map((doc, index) => ({
        id: index + 1,
        name: doc.filename,
        type: doc.file_extension.replace('.', '').toLowerCase(),
        size: doc.file_size_bytes,
        sizeMB: doc.file_size_mb,
        uploadedAt: doc.created_time,
        modifiedAt: doc.modified_time,
        filePath: doc.file_path,
        isPdf: doc.is_pdf,
        processed: true,
        tags: generateTagsFromFilename(doc.filename)
      }));

      setDocuments(transformedDocuments);
      setSummaryDocuments(summaryDocsMap);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate tags based on filename patterns
  const generateTagsFromFilename = (filename) => {
    const tags = [];
    const lowerFilename = filename.toLowerCase();

    // Add file type tag
    if (lowerFilename.includes('.pdf')) tags.push('pdf');
    if (lowerFilename.includes('.doc')) tags.push('document');
    if (lowerFilename.includes('.xls')) tags.push('spreadsheet');
    if (lowerFilename.includes('.ppt')) tags.push('presentation');

    // Add content-based tags
    if (lowerFilename.includes('report')) tags.push('report');
    if (lowerFilename.includes('analysis')) tags.push('analysis');
    if (lowerFilename.includes('financial')) tags.push('financial');
    if (lowerFilename.includes('earnings')) tags.push('earnings');
    if (lowerFilename.includes('annual')) tags.push('annual');
    if (lowerFilename.includes('quarterly')) tags.push('quarterly');
    if (lowerFilename.includes('test')) tags.push('test');

    // Add size-based tags
    if (lowerFilename.includes('js')) tags.push('javascript');

    return tags.length > 0 ? tags : ['document'];
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'docx':
      case 'doc': return '📝';
      case 'xlsx':
      case 'xls': return '📊';
      case 'pptx':
      case 'ppt': return '📈';
      default: return '📁';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || doc.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (docId) => {
    // TODO: Implement actual delete API call
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const handleViewDocument = async (document) => {
    console.log('Viewing document:', document.name);
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${BASE_URL}/api/reports/${document.name}`);
      console.log('API Response:', response.data);
      setReportData(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching report:', error);
      setError('Failed to fetch the report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
    setReportData(null); // Also clear report data on close
  };

  const handleRefresh = () => {
    fetchDocuments();
  };

  if (loading) {
    return (
      <div className="document-list">
        <div className="loading-placeholder">
          <div className="loading-spinner">⏳</div>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-list">
        <div className="error-placeholder">
          <div className="error-icon">❌</div>
          <p>Error loading documents: {error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="document-list">
      <div className="list-header">
        <div className="header-content">
          <div className="header-info">
            <h3>Document Library</h3>
            <p className="document-count">
              {documents.length} document{documents.length !== 1 ? 's' : ''} •
              Total size: {formatFileSize(documents.reduce((sum, doc) => sum + doc.size, 0))}
            </p>
          </div>
          <button onClick={handleRefresh} className="refresh-btn" title="Refresh documents">
            🔄
          </button>
        </div>
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="pdf">PDF</option>
            <option value="docx">DOCX</option>
            <option value="xlsx">XLSX</option>
            <option value="pptx">PPTX</option>
          </select>
        </div>
      </div>

      <div className="documents-container">
        {filteredDocuments.length === 0 ? (
          <div className="no-documents">
            <p>No documents found</p>
          </div>
        ) : (
          <div className="documents-grid">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-header">
                  <span className="file-icon">{getFileIcon(doc.type)}</span>
                  <div className="document-status">
                    {doc.processed ? (
                      <span className="status-badge processed">✓ Processed</span>
                    ) : (
                      <span className="status-badge processing">⏳ Processing</span>
                    )}
                  </div>
                </div>

                <div className="document-info">
                  <h4 className="document-name">{doc.name}</h4>
                  <p className="document-meta">
                    {formatFileSize(doc.size)} • {formatDate(doc.uploadedAt)}
                  </p>
                  <div className="document-tags">
                    {doc.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="document-actions">
                  <button
                    className="action-btn view-btn"
                    onClick={() => handleViewDocument(doc)}
                  >
                    View Summary
                  </button>
                  <button className="action-btn delete-btn" onClick={() => handleDelete(doc.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF Modal */}
      <ReportModal 
        isOpen={isModalOpen && reportData}
        onClose={closeModal}
        data={reportData}
      />
    </div>
  );
};

export default DocumentList; 